/**
 * Sidecart - Piano LED Theme
 * Based on Shandrani implementation
 * Clean, performant sidecart with AJAX cart, discount codes, and recommendations
 */

const SELECTORS = {
  container: '.js-sidecart',
  drawer: '[data-sidecart]',
  overlay: '[data-sidecart-overlay]',
  close: '[data-sidecart-close]',
  items: '[data-sidecart-items]',
  empty: '[data-sidecart-empty]',
  footer: '[data-sidecart-footer]',
  total: '[data-sidecart-total]',
  count: '[data-sidecart-count]',
  discountInput: '[data-sidecart-discount-input]',
  discountApply: '[data-sidecart-discount-apply]',
  discountMessage: '[data-sidecart-discount-message]',
  item: '[data-sidecart-item]',
  quantityMinus: '[data-sidecart-minus]',
  quantityPlus: '[data-sidecart-plus]',
  quantityValue: '[data-sidecart-quantity]',
  remove: '[data-sidecart-remove]',
  cartLink: 'a[href*="/cart"], [data-cart-icon], .cart-icon-bubble, [aria-controls="cart-drawer"]',
  cartCount: '[data-cart-count], cart-count, .cart-count'
};

const CLASSES = {
  open: 'is-open',
  loading: 'is-loading',
  removing: 'is-removing',
  bodyOpen: 'sidecart-open'
};

class Sidecart {
  constructor() {
    this.elements = this.cacheElements();
    if (!this.elements.drawer) return;

    this.state = {
      isOpen: false,
      isLoading: false,
      cart: null
    };

    this.config = {
      sectionId: this.elements.drawer?.dataset?.sectionId || 'w-sidecart'
    };

    this.init();
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  cacheElements() {
    const get = (selector) => document.querySelector(selector);
    return {
      container: get(SELECTORS.container),
      drawer: get(SELECTORS.drawer),
      overlay: get(SELECTORS.overlay),
      close: get(SELECTORS.close),
      items: get(SELECTORS.items),
      empty: get(SELECTORS.empty),
      footer: get(SELECTORS.footer),
      total: get(SELECTORS.total),
      count: get(SELECTORS.count),
      discountInput: get(SELECTORS.discountInput),
      discountApply: get(SELECTORS.discountApply),
      discountMessage: get(SELECTORS.discountMessage)
    };
  }

  async init() {
    this.bindEvents();
    this.interceptAddToCart();
    this.initCountdown();

    const cart = await this.fetchCart();
    if (cart) {
      this.state.cart = cart;
      this.safeCall(() => this.checkExistingDiscount(cart));
    }
  }

  // ============================================
  // COUNTDOWN TIMER
  // ============================================

  initCountdown() {
    const countdownEl = document.querySelector('[data-sidecart-countdown]');
    if (!countdownEl) return;

    const endDateStr = countdownEl.dataset.countdownDate;
    if (!endDateStr) return;

    const endDate = new Date(endDateStr).getTime();
    const daysEl = countdownEl.querySelector('[data-countdown-days]');
    const hoursEl = countdownEl.querySelector('[data-countdown-hours]');
    const minutesEl = countdownEl.querySelector('[data-countdown-minutes]');
    const secondsEl = countdownEl.querySelector('[data-countdown-seconds]');

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = endDate - now;

      if (distance < 0) {
        if (daysEl) daysEl.textContent = '00';
        if (hoursEl) hoursEl.textContent = '00';
        if (minutesEl) minutesEl.textContent = '00';
        if (secondsEl) secondsEl.textContent = '00';
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
      if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
    };

    updateCountdown();
    setInterval(updateCountdown, 1000);
  }

  // ============================================
  // EVENTS
  // ============================================

  bindEvents() {
    const { overlay, close, items, discountApply, discountInput } = this.elements;

    // Close events
    overlay?.addEventListener('click', () => this.close());
    close?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => e.key === 'Escape' && this.state.isOpen && this.close());

    // Cart link interception
    document.addEventListener('click', (e) => this.handleCartLinkClick(e), true);

    // Quantity & remove (delegation)
    items?.addEventListener('click', (e) => this.handleItemAction(e));

    // Discount
    discountApply?.addEventListener('click', () => this.applyDiscount());
    discountInput?.addEventListener('keydown', (e) => e.key === 'Enter' && (e.preventDefault(), this.applyDiscount()));

    // Custom events
    document.addEventListener('cart:add', () => this.open());
    document.addEventListener('product:added', () => (this.refreshCart(), this.open()));
    document.addEventListener('variant:add', () => (this.renderCartItems(), this.refreshCart(), this.open()));
    document.addEventListener('cart:change', () => (this.renderCartItems(), this.refreshCart(), this.open()));
  }

  handleCartLinkClick(e) {
    const link = e.target.closest(SELECTORS.cartLink);
    if (!link) return;
    if (link.href?.includes('/checkout')) return;

    const isCartLink = link.href && (link.href.endsWith('/cart') || link.href.includes('/cart?') || link.href.includes('/cart#'));
    const isCartIcon = link.hasAttribute('data-cart-icon') || link.classList.contains('cart-icon-bubble');
    const isCartDrawerTrigger = link.getAttribute('aria-controls') === 'cart-drawer';

    if (isCartLink || isCartIcon || isCartDrawerTrigger) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.open();
    }
  }

  handleItemAction(e) {
    const item = e.target.closest(SELECTORS.item);
    if (!item) return;

    const minus = e.target.closest(SELECTORS.quantityMinus);
    const plus = e.target.closest(SELECTORS.quantityPlus);
    const remove = e.target.closest(SELECTORS.remove);

    if (!minus && !plus && !remove) return;

    const key = item.dataset.lineKey;
    const quantityEl = item.querySelector(SELECTORS.quantityValue);
    let quantity = parseInt(quantityEl?.textContent) || 0;

    if (minus) quantity = Math.max(0, quantity - 1);
    else if (plus) quantity++;
    else if (remove) quantity = 0;

    this.updateQuantity(key, quantity, item);
  }

  // ============================================
  // DRAWER OPEN/CLOSE
  // ============================================

  open() {
    console.log('Sidecart: open() called', { isOpen: this.state.isOpen, elements: this.elements });
    if (this.state.isOpen) return;
    this.state.isOpen = true;

    const { container, drawer, overlay } = this.elements;
    console.log('Sidecart: Adding classes', { container, drawer, overlay });

    container?.classList.add(CLASSES.open);
    drawer?.classList.add(CLASSES.open);
    overlay?.classList.add(CLASSES.open);
    document.body.classList.add(CLASSES.bodyOpen);

    console.log('Sidecart: Classes added', {
      containerClasses: container?.className,
      drawerClasses: drawer?.className,
      overlayClasses: overlay?.className
    });

    drawer?.focus();
    this.refreshCart();
  }

  close() {
    if (!this.state.isOpen) return;
    this.state.isOpen = false;

    const { container, drawer, overlay } = this.elements;
    container?.classList.remove(CLASSES.open);
    drawer?.classList.remove(CLASSES.open);
    overlay?.classList.remove(CLASSES.open);
    document.body.classList.remove(CLASSES.bodyOpen);
  }

  // ============================================
  // CART API
  // ============================================

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      console.warn('Sidecart: Error fetching cart', e);
      return null;
    }
  }

  async refreshCart() {
    const cart = await this.fetchCart();
    if (cart) {
      this.state.cart = cart;
      this.updateUI(cart);
    }
  }

  async updateQuantity(key, quantity, itemElement) {
    if (this.state.isLoading) return;
    this.setLoading(true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) {
        console.error('Sidecart: Cart update failed', response.status);
        return;
      }

      const cart = await response.json();

      await this.renderCartItems();
      this.updateUI(cart);
      this.state.cart = cart;
    } catch (e) {
      console.error('Sidecart: Error updating quantity', e);
    } finally {
      this.setLoading(false);
    }
  }

  async renderCartItems() {
    try {
      const response = await fetch(`/?section_id=${this.config.sectionId}`);
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newItems = doc.querySelector(SELECTORS.items);
      if (newItems && this.elements.items) {
        this.elements.items.innerHTML = newItems.innerHTML;
      }

      const newCount = doc.querySelector(SELECTORS.count);
      if (newCount && this.elements.count) {
        this.elements.count.textContent = newCount.textContent;
      }

      // Update empty state
      const newEmpty = doc.querySelector(SELECTORS.empty);
      if (newEmpty && this.elements.empty) {
        this.elements.empty.innerHTML = newEmpty.innerHTML;
      }

      // Update footer
      const newFooter = doc.querySelector(SELECTORS.footer);
      if (newFooter && this.elements.footer) {
        this.elements.footer.innerHTML = newFooter.innerHTML;
        // Re-cache discount elements
        this.elements.discountInput = this.elements.footer.querySelector(SELECTORS.discountInput);
        this.elements.discountApply = this.elements.footer.querySelector(SELECTORS.discountApply);
        this.elements.discountMessage = this.elements.footer.querySelector(SELECTORS.discountMessage);
        // Re-bind discount events
        this.elements.discountApply?.addEventListener('click', () => this.applyDiscount());
        this.elements.discountInput?.addEventListener('keydown', (e) => e.key === 'Enter' && (e.preventDefault(), this.applyDiscount()));
        // Re-initialize countdown
        this.initCountdown();
      }
    } catch (e) {
      console.error('Sidecart: Error rendering cart items', e);
    }
  }

  // ============================================
  // UI UPDATES
  // ============================================

  updateUI(cart) {
    const isEmpty = cart.item_count === 0;
    const { empty, items, footer, total, count } = this.elements;

    // Toggle states
    this.setDisplay(empty, isEmpty, 'flex');
    this.setDisplay(items, !isEmpty, 'flex');
    this.setDisplay(footer, !isEmpty, 'block');

    // Update values
    if (total) total.textContent = this.formatMoney(cart.total_price);
    if (count) count.textContent = cart.item_count;

    this.updateGlobalCartCount(cart.item_count);
  }

  updateGlobalCartCount(count) {
    document.querySelectorAll(SELECTORS.cartCount).forEach(el => {
      el.textContent = count;
    });
  }

  setLoading(loading) {
    this.state.isLoading = loading;
    this.elements.drawer?.classList.toggle(CLASSES.loading, loading);
  }

  // ============================================
  // DISCOUNT CODES
  // ============================================

  async applyDiscount() {
    const { discountInput, discountApply } = this.elements;
    const code = discountInput?.value?.trim();
    if (!code) return;

    this.setDiscountButtonState(true, 'Vérification...');

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: code })
      });

      if (!response.ok) throw new Error('Failed to apply discount');

      const cart = await response.json();
      const isValid = (cart.discount_codes || []).some(d => d.code === code && d.applicable);

      if (!isValid) {
        this.showDiscountMessage('Code promo invalide', 'error');
        this.setDiscountButtonState(false);
        return;
      }

      this.showDiscountMessage('Code promo appliqué !', 'success');
      discountInput.value = '';
      await this.renderCartItems();
      this.updateUI(cart);
      this.displayActiveDiscount(code);
    } catch (e) {
      console.error('Sidecart: Error applying discount', e);
      this.showDiscountMessage('Erreur lors de l\'application', 'error');
    } finally {
      this.setDiscountButtonState(false);
    }
  }

  async removeDiscount() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: '' })
      });

      if (!response.ok) throw new Error('Failed to remove discount');

      const cart = await response.json();
      this.hideActiveDiscount();
      await this.renderCartItems();
      this.updateUI(cart);
      this.showDiscountMessage('Code promo retiré', 'success');
    } catch (e) {
      console.error('Sidecart: Error removing discount', e);
      this.showDiscountMessage('Erreur lors de la suppression', 'error');
    }
  }

  checkExistingDiscount(cartData = null) {
    const cart = cartData || this.state.cart;
    if (!cart) return;
    const activeDiscount = (cart.discount_codes || []).find(d => d.applicable);
    activeDiscount ? this.displayActiveDiscount(activeDiscount.code) : this.hideActiveDiscount();
  }

  displayActiveDiscount(code) {
    let el = document.querySelector('.sidecart__active-discount');

    if (!el) {
      el = document.createElement('div');
      el.className = 'sidecart__active-discount';
      document.querySelector('.sidecart__discount')?.parentNode?.insertBefore(el, document.querySelector('.sidecart__discount'));
    }

    el.innerHTML = `
      <div class="sidecart__active-discount-content">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <span class="p-small">Code promo : <strong>${code}</strong></span>
        <button type="button" class="sidecart__remove-discount" aria-label="Retirer le code promo">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M.317.315a1.08 1.08 0 011.525 0L5.006 3.476 8.158.329a1.08 1.08 0 111.526 1.523L6.532 4.999l3.152 3.149a1.08 1.08 0 11-1.525 1.523L5.006 6.522l-3.165 3.162a1.08 1.08 0 11-1.525-1.523l3.165-3.162L.317 1.838a1.08 1.08 0 010-1.523z" fill="currentColor" fill-opacity=".5"/></svg>
        </button>
      </div>
    `;
    el.style.display = 'flex';
    el.querySelector('.sidecart__remove-discount')?.addEventListener('click', () => this.removeDiscount());
  }

  hideActiveDiscount() {
    const el = document.querySelector('.sidecart__active-discount');
    if (el) el.style.display = 'none';
  }

  showDiscountMessage(message, type) {
    let el = this.elements.discountMessage;

    if (!el) {
      el = document.createElement('div');
      el.className = 'sidecart__discount-message';
      document.querySelector('.sidecart__discount')?.parentNode?.insertBefore(el, document.querySelector('.sidecart__discount')?.nextSibling);
      this.elements.discountMessage = el;
    }

    el.textContent = message;
    el.className = `sidecart__discount-message sidecart__discount-message--${type}`;
    el.style.display = 'block';

    setTimeout(() => el.style.display = 'none', 3000);
  }

  setDiscountButtonState(disabled, text = null) {
    const btn = this.elements.discountApply;
    if (!btn) return;
    btn.disabled = disabled;
    btn.textContent = text || btn.dataset.text || 'Appliquer';
  }

  // ============================================
  // ADD TO CART INTERCEPTION (via events only)
  // ============================================

  interceptAddToCart() {
    // We rely on custom events dispatched by ATC scripts (variant:add, cart:change)
    // No fetch override needed - it conflicts with Shopify's scripts
  }

  // ============================================
  // HELPERS
  // ============================================

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    if (window.Shopify?.currency?.active) {
      return new Intl.NumberFormat(Shopify.locale || 'fr-FR', {
        style: 'currency',
        currency: Shopify.currency.active
      }).format(amount);
    }
    return `${amount.replace('.', ',')}€`;
  }

  setDisplay(el, show, displayType = 'block') {
    if (el) el.style.display = show ? displayType : 'none';
  }

  safeCall(fn) {
    try { fn(); } catch (e) { console.warn('Sidecart: Non-critical error', e); }
  }
}

// ============================================
// INITIALIZATION
// ============================================

let sidecartInitialized = false;

function initSidecart() {
  if (sidecartInitialized) return;
  sidecartInitialized = true;
  window.sidecart = new Sidecart();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSidecart);
} else {
  initSidecart();
}

// ============================================
// SHIPPING CALCULATOR (shared logic)
// ============================================

function initShippingCalculators() {
  const sidecart = document.querySelector('.js-sidecart, [data-sidecart]');
  if (!sidecart) return;

  sidecart.querySelectorAll('shipping-calculator').forEach(el => {
    if (el.dataset.initialized) return;
    el.dataset.initialized = 'true';

    const delay = parseInt(el.dataset.shippingDelay) || 3;
    const dateSpan = el.querySelector('[data-shipping-date]');
    if (!dateSpan) return;

    const today = new Date();
    let daysAdded = 0;
    while (daysAdded < delay) {
      today.setDate(today.getDate() + 1);
      const dow = today.getDay();
      if (dow !== 0 && dow !== 6) daysAdded++;
    }

    const jours = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
    const mois = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    dateSpan.textContent = `${jours[today.getDay()]} ${today.getDate()} ${mois[today.getMonth()]}`;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingCalculators);
} else {
  initShippingCalculators();
}

// Re-init after sidecart opens (dynamic content)
new MutationObserver(() => initShippingCalculators())
  .observe(document.body, { childList: true, subtree: true });

